import { useMemo } from 'react'
import { Input, Dropdown, Option, OptionGroup, Switch, Button } from '@fluentui/react-components'
import { Dismiss16Regular, Search20Regular } from '@fluentui/react-icons'
import { SERVICE_LABELS } from '../lib/services'
import { licenseTypeDisplayName } from '../lib/personRows'
import type { EntraStatus, StoredFilters } from '../lib/personRows'
import type { LicenseTypeConfig, OrphanCheckResult } from '../types'

const STATUS_OPTIONS: { value: EntraStatus; label: string }[] = [
  { value: 'enabled', label: 'Active' },
  { value: 'disabled', label: 'Blocked' },
  { value: 'unknown', label: 'Missing in Entra' },
]
const STATUS_LABEL: Record<EntraStatus, string> = Object.fromEntries(
  STATUS_OPTIONS.map(o => [o.value, o.label]),
) as Record<EntraStatus, string>

// Synthetic option value for a per-service "select all" row in the License type filter — never
// persisted in licenseFilter itself, only ever derived from/expanded against it.
const ALL_PREFIX = '__all__:'

/**
 * The full search/status/license-type/visibility filter bar for a Licenses-style table — shared
 * by pages/Licenses.tsx (serviceIds = every service) and components/ServiceLicensesTab.tsx
 * (serviceIds = just that one service, so the License type dropdown renders a single group).
 */
export default function LicenseFilterBar(props: {
  serviceIds: string[]
  results: OrphanCheckResult[]
  configByService: Record<string, Record<string, LicenseTypeConfig>>
  filters: StoredFilters
  onChange: (patch: Partial<StoredFilters>) => void
  /** Only pages/Licenses.tsx passes this — the per-service icon cells there can mix a person's
   * own licenses with ones attributed from shared/external accounts they're Responsible for,
   * which isn't always wanted. ServiceLicensesTab has no equivalent concept (its rows are raw
   * accounts, not merged people), so it omits the prop and the switch stays hidden there. */
  showAttributionToggle?: boolean
  /** ServiceLicensesTab passes this — it already has its own search box (shared with User links
   * via ProblemFilterBar, matching by email or name), so this one would just be a confusing
   * duplicate there. pages/Licenses.tsx has no equivalent and omits the prop. */
  hideSearch?: boolean
}) {
  const { serviceIds, results, configByService, filters, onChange, showAttributionToggle, hideSearch } = props
  const { search, statusFilter, licenseFilter, showNoLicense, showGuests, hideAttributedLicenses } = filters

  // License types for the filter dropdown, grouped by which service reports them — a license
  // type string is only ever compared within its own service's group, so two services happening
  // to share a literal name (unlikely, but not guaranteed unique) don't merge into one option.
  const licenseTypesByService = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const id of serviceIds) {
      map[id] = [...new Set(results.filter(r => r.serviceId === id).map(r => r.licenseType))].sort((a, b) =>
        licenseTypeDisplayName(a, configByService).localeCompare(licenseTypeDisplayName(b, configByService)),
      )
    }
    return map
  }, [results, serviceIds, configByService])

  // Adds a synthetic "select all" key for each service whose every license type is already
  // selected, so that row renders checked — purely a display concern, never written back to
  // licenseFilter itself.
  const licenseDropdownSelection = useMemo(() => {
    const allKeys = serviceIds
      .filter(id => licenseTypesByService[id]?.length && licenseTypesByService[id].every(lt => licenseFilter.includes(lt)))
      .map(id => `${ALL_PREFIX}${id}`)
    return [...licenseFilter, ...allKeys]
  }, [licenseFilter, serviceIds, licenseTypesByService])

  // A fixed-length summary instead of the full comma-joined list — otherwise the trigger grows
  // (and its popup jumps) with every extra license selected. Fluent's Dropdown renders the value
  // as a bare text node inside a CSS-grid button, which can't be truncated with text-overflow:
  // ellipsis (that only works on real block/inline boxes, not the anonymous box a grid container
  // generates for a text child), so this caps it in plain string logic instead.
  const licenseFilterValue = useMemo(() => {
    if (licenseFilter.length === 0) return ''
    if (licenseFilter.length === 1) return licenseTypeDisplayName(licenseFilter[0], configByService)
    return `${licenseFilter.length} license types selected`
  }, [licenseFilter, configByService])

  function handleLicenseFilterSelect(optionValue: string, selectedOptions: string[]) {
    if (optionValue.startsWith(ALL_PREFIX)) {
      const serviceId = optionValue.slice(ALL_PREFIX.length)
      const groupTypes = licenseTypesByService[serviceId] ?? []
      const groupFullySelected = groupTypes.every(lt => licenseFilter.includes(lt))
      onChange({
        licenseFilter: groupFullySelected
          ? licenseFilter.filter(lt => !groupTypes.includes(lt))
          : [...new Set([...licenseFilter, ...groupTypes])],
      })
      return
    }
    onChange({ licenseFilter: selectedOptions.filter(v => !v.startsWith(ALL_PREFIX)) })
  }

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '24px 0' }}>
      {!hideSearch && (
        <Input
          contentBefore={<Search20Regular />}
          contentAfter={
            search && (
              <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss16Regular />}
                onClick={() => onChange({ search: '' })}
                aria-label="Clear search"
              />
            )
          }
          placeholder="Search by name or email..."
          value={search}
          onChange={(_e, d) => onChange({ search: d.value })}
          style={{ minWidth: 240 }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Dropdown
          placeholder="Status"
          multiselect
          selectedOptions={statusFilter}
          value={statusFilter.map(s => STATUS_LABEL[s as EntraStatus]).join(', ')}
          onOptionSelect={(_e, d) => onChange({ statusFilter: d.selectedOptions })}
          style={{ minWidth: 180 }}
        >
          {STATUS_OPTIONS.map(o => (
            <Option key={o.value} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Dropdown>
        {statusFilter.length > 0 && (
          <Button
            appearance="transparent"
            size="small"
            icon={<Dismiss16Regular />}
            onClick={() => onChange({ statusFilter: [] })}
            aria-label="Clear status filter"
          />
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Dropdown
          placeholder="License type"
          multiselect
          selectedOptions={licenseDropdownSelection}
          value={licenseFilterValue}
          onOptionSelect={(_e, d) => handleLicenseFilterSelect(d.optionValue as string, d.selectedOptions)}
          style={{ width: 260 }}
          listbox={{ style: { minWidth: 340, maxWidth: 420 } }}
        >
          {serviceIds.map(id => (
            // OptionGroup already draws its own divider between groups (:not(:last-child)), no
            // extra border needed here.
            <OptionGroup key={id} label={SERVICE_LABELS[id] ?? id}>
              <Option value={`${ALL_PREFIX}${id}`} text={`All ${SERVICE_LABELS[id] ?? id}`}>
                <strong>All {SERVICE_LABELS[id] ?? id}</strong>
              </Option>
              {(licenseTypesByService[id] ?? []).map(lt => (
                <Option key={lt} value={lt} text={licenseTypeDisplayName(lt, configByService)}>
                  {licenseTypeDisplayName(lt, configByService)}
                </Option>
              ))}
            </OptionGroup>
          ))}
        </Dropdown>
        {licenseFilter.length > 0 && (
          <Button
            appearance="transparent"
            size="small"
            icon={<Dismiss16Regular />}
            onClick={() => onChange({ licenseFilter: [] })}
            aria-label="Clear license type filter"
          />
        )}
      </div>
      <Switch
        label="Show users without licenses"
        checked={showNoLicense}
        onChange={(_e, d) => onChange({ showNoLicense: d.checked })}
      />
      <Switch
        label="Show guests"
        checked={showGuests}
        onChange={(_e, d) => onChange({ showGuests: d.checked })}
      />
      {showAttributionToggle && (
        <Switch
          label="Hide shared/external licenses"
          checked={hideAttributedLicenses}
          onChange={(_e, d) => onChange({ hideAttributedLicenses: d.checked })}
        />
      )}
    </div>
  )
}
